import React, { useState } from "react";
import * as styles from "./RoleSetupPanelComponent.module.scss";
import { Panel } from "cprime-foundational-components";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store/store";
import {
  editApiRelation,
  createApiRelation,
  deleteApiRelation,
  createFeatureRelation,
  deleteFeatureRelation,
} from "../../store/slices/CreateRoleSlice";
import { triggerToast } from "../../utils/toast";
import { incrementTableRefresh } from "../../store/slices/apiDetailsSlice";

import Button from "../Common/CprimeButton";
const scssClass = styles as any;

interface RoleSetupPanleComponentProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit?: () => any;
  BodyComponent: React.ReactNode;
  tabValue: number;
  isEdit: boolean;
  isExtraButton?: boolean;
  extraButtons?: React.ReactNode;
  submitLabel?: string | boolean;
  isSubmitDisable?: boolean;
}

const RoleSetupPanleComponent: React.FC<RoleSetupPanleComponentProps> = ({
  open,
  title,
  onClose,
  onSubmit = () => {},
  BodyComponent,
  tabValue,
  isEdit,
  isExtraButton = false,
  extraButtons = <></>,
  submitLabel = false,
  isSubmitDisable = false,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isApiLoading, setIsApiLoading] = useState(false);
  const [isFeatureLoading, setIsFeatureLoading] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const editPayload = useSelector(
    (state: RootState) => state.createRole.editApiRelationsPayload,
  );
  const createApiPayload = useSelector(
    (state: RootState) => state.createRole.createApiRelationsPayload,
  );

  const createFeaturePayload = useSelector(
    (state: RootState) => state.createRole.featureRelations.createEditPayload,
  );

  const handleCreateFeatureRelation = async (createFeaturePayload: any) => {
    const createPayload = {
      ...createFeaturePayload,
      permissionIds: createFeaturePayload?.permissionIds?.map(
        (item: { id: any }) => item?.id,
      ),
    };
    try {
      setIsFeatureLoading(true);
      const response = await dispatch(createFeatureRelation(createPayload));
      if (response.payload) {
        triggerToast(
          response.payload.message,
          response.payload.status === "SUCCESS" ? "success" : "error",
        );
      }
      onClose();
    } catch (error) {
      triggerToast(`Failed to save Feature Relations`, "error");
    } finally {
      setIsFeatureLoading(false);
    }
  };

  const handleDeleteFeatureRelation = async (deleteFeaturePayload: any) => {
    try {
      setIsDeleteLoading(true);
      await dispatch(deleteFeatureRelation(deleteFeaturePayload));
      triggerToast(`Feature Relations deleted successfully`, "success");
      onClose();
    } catch (error) {
      triggerToast(`Failed to delete Feature Relations`, "error");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const handleCreateEditApiRelation = async () => {
    try {
      setIsApiLoading(true);
      let response;
      if (isEdit) {
        response = await dispatch(editApiRelation(editPayload)).unwrap();
        triggerToast("API relation updated successfully", "success");
        onClose();
        dispatch(incrementTableRefresh());
      } else {
        response = await dispatch(createApiRelation(createApiPayload)).unwrap();
        if (response.status === true) {
          triggerToast("API relation created successfully", "success");
          onClose();
          dispatch(incrementTableRefresh());
        }
        if (response.status === false) {
          triggerToast(`${response.error}`, "error");
        }
      }
    } catch (error) {
      triggerToast(
        `Error ${isEdit ? "updating" : "creating"} API relation`,
        "error",
      );
    } finally {
      setIsApiLoading(false);
    }
  };

  const handleDeleteSingleAPI = async () => {
    try {
      setIsDeleteLoading(true);
      const ids = [editPayload?.id];
      const response = await dispatch(deleteApiRelation(ids)).unwrap();
      if (response.error) {
        triggerToast(`${response.error}`, "error");
      } else if (response.status === true) {
        dispatch(incrementTableRefresh());
        triggerToast(`${response.message}`, "success");
        onClose();
      }
    } catch (error: any) {
      triggerToast("Error deleting API relation(s)", "error");
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const renderSlotComponent = () => {
    return (
      <>
        <div className={scssClass.footerButtons}>
          <div className={scssClass.leftButtons}>
            {isEdit && (
              <Button
                variant="danger"
                onClick={() => {
                  if (tabValue === 1) {
                    const deleteRoleFeatureRelations = [
                      {
                        roleId: createFeaturePayload.roleId,
                        featureId: createFeaturePayload.featureId,
                        associatedGroup:
                          createFeaturePayload.oldAssociatedGroup ?? [],
                      },
                    ];
                    handleDeleteFeatureRelation(deleteRoleFeatureRelations);
                  }
                  if (tabValue === 2) {
                    handleDeleteSingleAPI();
                  }
                }}
                loading={isDeleteLoading}
              >
                {submitLabel
                  ? submitLabel
                  : tabValue === 1
                    ? "Delete Feature"
                    : tabValue === 2
                      ? "Delete API"
                      : ""}
              </Button>
            )}
          </div>
          <div className={scssClass.rightButtons}>
            <Button
              variant="tertiary"
              sx={{ marginRight: "10px" }}
              onClick={onClose}
            >
              Cancel
            </Button>
            {isExtraButton && extraButtons}
            <Button
              variant="primary"
              sx={{ marginLeft: "5px", marginRight: "10px" }}
              onClick={() => {
                if (tabValue === 2) {
                  handleCreateEditApiRelation();
                } else if (tabValue === 1) {
                  handleCreateFeatureRelation(createFeaturePayload);
                } else {
                  onSubmit();
                }
              }}
              disabled={isSubmitDisable}
              loading={
                tabValue === 2
                  ? isApiLoading
                  : tabValue === 1
                    ? isFeatureLoading
                    : false
              }
            >
              {submitLabel
                ? submitLabel
                : tabValue === 2
                  ? "Save API"
                  : tabValue === 1
                    ? "Save Feature"
                    : ""}
            </Button>
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      <Panel
        id="panelStyle"
        sx={{
          "& .MuiDrawer-root": { padding: "0px" },
          "& .MuiPaper-root": {
            width: "800px",
            top: "104px",
            height: "calc(100vh - 104px)",
            gap: "24px",
          },
        }}
        open={open}
        title={title}
        side="right"
        onClose={onClose}
        body={
          <>
            {BodyComponent}
            {renderSlotComponent()}
          </>
        }
      />
      <span
        style={{
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          position: "fixed",
          width: "100vw",
          height: "calc(100vh - 104px)",
          zIndex: 1100,
          top: "104px",
          left: 0,
        }}
      ></span>
    </div>
  );
};

export default RoleSetupPanleComponent;
